import React from 'react';
import { HStack, Icon, Alert, useBreakpointValue } from '@chakra-ui/react';
import { FiCheckCircle, FiAlertCircle, FiX } from 'react-icons/fi';
import Button from './Button';
import { ALERT_DURATIONS } from '../../constants/alerts';

export type AlertType = 'success' | 'error' | 'warning' | 'info';

export interface AlertConfig {
    autoHide: boolean;
    duration: number;
}

export interface AlertData {
    type: AlertType;
    message: string;
    id?: string;
    config?: Partial<AlertConfig>;
}

interface AlertProps {
    alert: AlertData;
    onClose: (id?: string) => void;
}

// Константы по умолчанию
export const DEFAULT_ALERT_CONFIG: AlertConfig = {
    autoHide: true,
    duration: ALERT_DURATIONS.NORMAL, // 3 секунды
};

const getAlertIcon = (type: AlertType) => {
    switch (type) {
        case 'success':
            return FiCheckCircle;
        case 'error':
            return FiAlertCircle;
        case 'warning':
            return FiAlertCircle;
        case 'info':
            return FiCheckCircle;
        default:
            return FiCheckCircle;
    }
};

const getAlertColor = (type: AlertType) => {
    switch (type) {
        case 'success':
            return 'green.500';
        case 'error':
            return 'red.500';
        case 'warning':
            return 'orange.500';
        case 'info':
            return 'blue.500';
        default:
            return 'blue.500';
    }
};

const AlertComponent: React.FC<AlertProps> = ({ alert, onClose }) => {
    const IconComponent = getAlertIcon(alert.type);
    const iconColor = getAlertColor(alert.type);

    // Адаптивные размеры для мобильных устройств
    const iconSize = useBreakpointValue({ base: 5, md: 4 });

    return (
        <Alert.Root
            status={alert.type}
            alignItems="center"
            justifyContent="space-between"
            cursor="pointer"
            onClick={() => onClose(alert.id)}
            p={{ base: 4, md: 3 }}
        >
            <HStack gap={{ base: 3, md: 2 }} flex="1">
                <Alert.Indicator>
                    <Icon as={IconComponent} color={iconColor} boxSize={iconSize} />
                </Alert.Indicator>
                <Alert.Title fontSize={{ base: 'md', md: 'sm' }} flex="1">
                    {alert.message}
                </Alert.Title>
            </HStack>
            <Button
                size="sm"
                buttonType="ghost"
                _hover={{ bg: 'transparent' }}
                onClick={(e) => {
                    e.stopPropagation();
                    onClose(alert.id);
                }}
                ml={2}
            >
                <Icon as={FiX} boxSize={iconSize} />
            </Button>
        </Alert.Root>
    );
};

export default AlertComponent;
